import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, DollarSign, Calendar, TrendingUp, ArrowLeft, Building2, TrendingDown, Map, BarChart3, Activity, Bitcoin, Shield, Target } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import PropertyMap from '../components/PropertyMap';
import DeRiskAnalysisPanel from '../components/DeRiskAnalysisPanel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CREPropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [btcPrice, setBtcPrice] = useState(70000);
  
  // Monte Carlo Parameters
  const [ltv, setLtv] = useState(65);
  const [bitcoinCollateralPct, setBitcoinCollateralPct] = useState(37);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTermYears, setLoanTermYears] = useState(3);
  const [defaultProbability, setDefaultProbability] = useState(10);
  const [btcExpectedReturn, setBtcExpectedReturn] = useState(25);
  const [btcVolatility, setBtcVolatility] = useState(65);
  const [propertyExpectedReturn, setPropertyExpectedReturn] = useState(8);
  const [propertyVolatility, setPropertyVolatility] = useState(20);
  const [numSimulations, setNumSimulations] = useState(1000);
  const [aggregateHaircut, setAggregateHaircut] = useState(55);
  
  // Simulation Results
  const [isSimulating, setIsSimulating] = useState(false);
  const [results, setResults] = useState(null);
  
  // De-Risk Panel
  const [showDeRiskPanel, setShowDeRiskPanel] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchBtcPrice();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cre/cre-properties/${id}`);
      if (response.data.success) {
        const prop = response.data.property;
        setProperty(prop);
        // Pre-populate with property data
        if (prop.ltv_ratio) setLtv(prop.ltv_ratio);
        if (prop.interest_rate) setInterestRate(prop.interest_rate);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBtcPrice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crypto/prices`);
      if (response.data.success) {
        setBtcPrice(response.data.prices.BTC.price);
      }
    } catch (error) {
      console.error('Error fetching BTC price:', error);
    }
  };

  const handleApplyForLoan = () => {
    const user = localStorage.getItem('user');
    if (!user) {
      navigate('/login', { state: { from: `/cre-property/${id}` } });
    } else {
      navigate(`/apply-cre/${id}`);
    }
  };

  // Calculated values
  const propertyValue = property?.property_value || 10000000;
  const loanAmount = propertyValue * (ltv / 100);
  const bitcoinCollateral = loanAmount * (bitcoinCollateralPct / 100);
  const btcRequired = bitcoinCollateral / btcPrice;
  const usableBtcCollateral = bitcoinCollateral * (1 - aggregateHaircut / 100);
  const reEquity = propertyValue - loanAmount;
  const totalCollateral = usableBtcCollateral + Math.max(0, reEquity);
  const coverageRatio = (totalCollateral / loanAmount) * 100;

  // Monte Carlo Simulation
  const runMonteCarloSimulation = () => {
    setIsSimulating(true);
    
    setTimeout(() => {
      const simResults = performMonteCarloSimulation();
      setResults(simResults);
      setIsSimulating(false);
    }, 100);
  };

  const performMonteCarloSimulation = () => {
    let bankProfits = [];
    let coverageRatios = [];
    let defaultCount = 0;
    let bankLossCount = 0;
    let finalBtcPrices = [];

    for (let i = 0; i < numSimulations; i++) {
      const defaulted = Math.random() < (defaultProbability / 100);
      
      const dt = loanTermYears;
      const drift = (btcExpectedReturn / 100) * dt;
      const randomShock = (btcVolatility / 100) * Math.sqrt(dt) * randomNormal();
      const btcReturn = Math.exp(drift + randomShock);
      const finalBtcPrice = btcPrice * btcReturn;
      finalBtcPrices.push(finalBtcPrice);
      
      const propertyDrift = (propertyExpectedReturn / 100) * dt;
      const propertyShock = (propertyVolatility / 100) * Math.sqrt(dt) * randomNormal();
      const propertyReturn = Math.exp(propertyDrift + propertyShock);
      const finalPropertyValue = propertyValue * propertyReturn;

      const finalBtcValue = btcRequired * finalBtcPrice;
      const finalUsableBtc = finalBtcValue * (1 - aggregateHaircut / 100);
      const finalReEquity = finalPropertyValue - loanAmount;
      const finalTotalCollateral = finalUsableBtc + Math.max(0, finalReEquity);
      const finalCoverage = (finalTotalCollateral / loanAmount) * 100;
      coverageRatios.push(finalCoverage);

      if (defaulted) {
        defaultCount++;
        const btcRecovery = btcRequired * finalBtcPrice;
        const propertyRecovery = finalPropertyValue * 0.85;
        const totalRecovery = btcRecovery + propertyRecovery;
        const bankProfit = totalRecovery - loanAmount - (loanAmount * 0.05);
        bankProfits.push(bankProfit);
        if (bankProfit < 0) bankLossCount++;
      } else {
        const interestEarned = loanAmount * (interestRate / 100) * loanTermYears;
        bankProfits.push(interestEarned);
      }
    }

    const bankMeanProfit = mean(bankProfits);
    const meanBtcPrice = mean(finalBtcPrices);
    const btcPriceChange = ((meanBtcPrice - btcPrice) / btcPrice) * 100;
    const meanCoverageRatio = mean(coverageRatios);

    return {
      bank: {
        meanProfit: bankMeanProfit,
        medianProfit: median(bankProfits),
        percentile5: percentile(bankProfits, 5),
        percentile95: percentile(bankProfits, 95),
        lossProbability: (bankLossCount / numSimulations) * 100,
        roi: (bankMeanProfit / loanAmount) * 100
      },
      defaultRate: (defaultCount / numSimulations) * 100,
      meanBtcPrice,
      btcPriceChange,
      meanCoverageRatio,
      minCoverage: Math.min(...coverageRatios),
      maxCoverage: Math.max(...coverageRatios)
    };
  };

  const randomNormal = () => {
    let u = 0, v = 0;
    while(u === 0) u = Math.random();
    while(v === 0) v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  };

  const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const median = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  const percentile = (arr, p) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const index = Math.floor((p / 100) * sorted.length);
    return sorted[index];
  };

  const fmt = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}K`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-[#2A2A33] text-xl">Loading property...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-[#2A2A33] text-xl">Property not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate('/real-cre')}
            variant="outline"
            className="border-[#006AFF] text-[#006AFF] hover:bg-[#006AFF] hover:text-white mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to CRE Properties
          </Button>

          {/* Property Header */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-sm text-[#006AFF] font-medium">{property.property_type.toUpperCase()}</div>
                  {property.is_distressed && (
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                      <TrendingDown className="h-3 w-3 mr-1" />
                      Distressed
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-[#2A2A33] mb-2">{property.name}</h1>
                <div className="flex items-center text-[#6B6B76]">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>{property.address}, {property.city}, {property.state}</span>
                </div>
              </div>
              <div className="mt-4 md:mt-0 text-right">
                <div className="text-sm text-[#6B6B76]">Property Value</div>
                <div className="text-3xl font-bold text-[#2A2A33]">${property.property_value.toLocaleString()}</div>
                <div className="text-sm text-[#6B6B76]">${property.price_per_sqft.toFixed(2)}/sqft</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'details', label: 'Property Details', icon: Building2 },
              { id: 'analysis', label: 'Deal Analysis', icon: BarChart3 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-5 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#006AFF] text-white' 
                    : 'bg-white text-[#6B6B76] hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Property Details Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <Card className="bg-white border-gray-200 p-6 mb-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Property Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#F7F7F7] rounded-xl p-4">
                      <div className="text-sm text-[#6B6B76] mb-1">Size</div>
                      <div className="text-xl font-semibold text-[#2A2A33]">{property.size_sqft.toLocaleString()} SF</div>
                    </div>
                    <div className="bg-[#F7F7F7] rounded-xl p-4">
                      <div className="text-sm text-[#6B6B76] mb-1">Year Built</div>
                      <div className="text-xl font-semibold text-[#2A2A33]">{property.year_built}</div>
                    </div>
                    <div className="bg-[#F7F7F7] rounded-xl p-4">
                      <div className="text-sm text-[#6B6B76] mb-1">Occupancy</div>
                      <div className={`text-xl font-semibold ${property.occupancy_rate < 60 ? 'text-red-600' : 'text-[#2A2A33]'}`}>
                        {property.occupancy_rate}%
                      </div>
                    </div>
                    <div className="bg-[#F7F7F7] rounded-xl p-4">
                      <div className="text-sm text-[#6B6B76] mb-1">Cap Rate</div>
                      <div className="text-xl font-semibold text-[#2A2A33]">{property.cap_rate}%</div>
                    </div>
                  </div>

                  <div className="bg-[#E8F4FD] rounded-xl p-4 mb-6">
                    <div className="text-sm text-[#6B6B76] mb-1">Net Operating Income (NOI)</div>
                    <div className="text-2xl font-semibold text-[#2A2A33]">${property.noi.toLocaleString()}</div>
                    <div className="text-xs text-[#6B6B76] mt-1">Annual</div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center text-[#2A2A33] mb-3">
                      <Map className="h-5 w-5 mr-2 text-[#006AFF]" />
                      <span className="font-semibold">Property Location</span>
                    </div>
                    <PropertyMap property={property} height="200px" />
                  </div>
                </Card>
              </div>

              <div>
                <Card className="bg-white border-gray-200 p-6 mb-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Loan Information</h3>
                  
                  <div className="bg-[#E8F4FD] rounded-xl p-4 mb-6">
                    <div className="text-sm text-[#6B6B76] mb-1">Current Loan Amount</div>
                    <div className="text-3xl font-bold text-[#2A2A33]">
                      ${property.loan_amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <div className="text-[#6B6B76]">LTV Ratio</div>
                        <div className="text-[#2A2A33] font-semibold text-lg">{property.ltv_ratio}%</div>
                      </div>
                      <div>
                        <div className="text-[#6B6B76]">Interest Rate</div>
                        <div className="text-[#2A2A33] font-semibold text-lg">{property.interest_rate}%</div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleApplyForLoan}
                    className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white py-5 text-lg mb-4 rounded-lg"
                    data-testid="apply-loan-btn"
                  >
                    Apply for Bitcoin-Backed Loan
                  </Button>

                  <Button 
                    onClick={() => setActiveTab('analysis')}
                    variant="outline"
                    className="w-full border-[#006AFF] text-[#006AFF] hover:bg-[#006AFF] hover:text-white py-5 text-lg rounded-lg mb-4"
                    data-testid="run-analysis-btn"
                  >
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Run Deal Analysis
                  </Button>

                  <Button 
                    onClick={() => setShowDeRiskPanel(true)}
                    className="w-full bg-gradient-to-r from-[#006AFF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#004499] text-white py-5 text-lg rounded-lg"
                    data-testid="derisk-analysis-btn"
                  >
                    <Shield className="h-5 w-5 mr-2" />
                    AI De-Risk Analysis
                  </Button>
                </Card>

                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Property Status</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-[#6B6B76]">Status</span>
                      <span className={`font-semibold ${property.is_distressed ? 'text-red-500' : 'text-green-600'}`}>
                        {property.status}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-[#6B6B76]">City</span>
                      <span className="text-[#2A2A33] font-semibold">{property.city}, {property.state}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-[#6B6B76]">Submarket</span>
                      <span className="text-[#2A2A33] font-semibold">{property.submarket}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-[#6B6B76]">Last Updated</span>
                      <span className="text-[#2A2A33] font-semibold">
                        {new Date(property.last_updated).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Deal Analysis Tab */}
          {activeTab === 'analysis' && (
            <div>
              {/* Property Summary */}
              <Card className="bg-white border-gray-200 p-6 mb-6 shadow-sm">
                <div className="grid md:grid-cols-5 gap-4">
                  <div className="bg-[#F7F7F7] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#6B6B76]">Property Value</div>
                    <div className="text-xl font-bold text-[#2A2A33]">{fmt(propertyValue)}</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#6B6B76]">Loan Amount</div>
                    <div className="text-xl font-bold text-[#2A2A33]">{fmt(loanAmount)}</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#6B6B76]">BTC Required</div>
                    <div className="text-xl font-bold text-[#006AFF]">₿ {btcRequired.toFixed(2)}</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#6B6B76]">Coverage Ratio</div>
                    <div className={`text-xl font-bold ${coverageRatio >= 100 ? 'text-green-600' : 'text-red-600'}`}>
                      {coverageRatio.toFixed(0)}%
                    </div>
                  </div>
                  <div className="bg-[#E8F4FD] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#6B6B76]">Live BTC Price</div>
                    <div className="text-xl font-bold text-[#006AFF]">${btcPrice.toLocaleString()}</div>
                  </div>
                </div>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Parameters */}
                <div className="space-y-6">
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Deal Parameters</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Loan-to-Value (LTV)</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{ltv}%</span>
                        </div>
                        <Slider value={[ltv]} onValueChange={(val) => setLtv(val[0])} min={20} max={150} step={5} />
                        <div className="text-xs text-[#6B6B76] mt-1">Loan: {fmt(loanAmount)}</div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Bitcoin Collateral %</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{bitcoinCollateralPct}% of Loan</span>
                        </div>
                        <Slider value={[bitcoinCollateralPct]} onValueChange={(val) => setBitcoinCollateralPct(val[0])} min={20} max={60} step={1} />
                        <div className="text-xs text-[#6B6B76] mt-1">{fmt(bitcoinCollateral)} ({btcRequired.toFixed(2)} BTC)</div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">BTC Haircut</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{aggregateHaircut}%</span>
                        </div>
                        <Slider value={[aggregateHaircut]} onValueChange={(val) => setAggregateHaircut(val[0])} min={30} max={70} step={5} />
                        <div className="text-xs text-[#6B6B76] mt-1">Usable: {fmt(usableBtcCollateral)}</div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Interest Rate</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{interestRate}% APR</span>
                        </div>
                        <Slider value={[interestRate]} onValueChange={(val) => setInterestRate(val[0])} min={3} max={15} step={0.5} />
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Loan Term</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{loanTermYears} years</span>
                        </div>
                        <Slider value={[loanTermYears]} onValueChange={(val) => setLoanTermYears(val[0])} min={1} max={10} step={1} />
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Default Probability</label>
                          <span className={`text-sm font-semibold ${defaultProbability > 20 ? 'text-red-600' : 'text-[#2A2A33]'}`}>{defaultProbability}%</span>
                        </div>
                        <Slider value={[defaultProbability]} onValueChange={(val) => setDefaultProbability(val[0])} min={0} max={50} step={1} />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Market Assumptions</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">BTC Expected Return</label>
                          <span className={`text-sm font-semibold ${btcExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{btcExpectedReturn}%</span>
                        </div>
                        <Slider value={[btcExpectedReturn]} onValueChange={(val) => setBtcExpectedReturn(val[0])} min={-50} max={100} step={5} />
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">BTC Volatility</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{btcVolatility}%</span>
                        </div>
                        <Slider value={[btcVolatility]} onValueChange={(val) => setBtcVolatility(val[0])} min={20} max={120} step={5} />
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Property Expected Return</label>
                          <span className={`text-sm font-semibold ${propertyExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{propertyExpectedReturn}%</span>
                        </div>
                        <Slider value={[propertyExpectedReturn]} onValueChange={(val) => setPropertyExpectedReturn(val[0])} min={-30} max={30} step={1} />
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="text-sm text-[#6B6B76]">Simulations</label>
                          <span className="text-sm font-semibold text-[#2A2A33]">{numSimulations.toLocaleString()}</span>
                        </div>
                        <Slider value={[numSimulations]} onValueChange={(val) => setNumSimulations(val[0])} min={100} max={5000} step={100} />
                      </div>
                    </div>
                  </Card>

                  <Button 
                    onClick={runMonteCarloSimulation}
                    disabled={isSimulating}
                    className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white py-6 text-lg"
                    data-testid="run-simulation-btn"
                  >
                    {isSimulating ? (
                      <><Activity className="mr-2 h-5 w-5 animate-spin" /> Running Simulation...</>
                    ) : (
                      <><BarChart3 className="mr-2 h-5 w-5" /> Run Monte Carlo Simulation</>
                    )}
                  </Button>
                </div>

                {/* Results */}
                <div className="space-y-6">
                  {results ? (
                    <>
                      <Card className="bg-[#006AFF] text-white p-6 shadow-sm">
                        <div className="flex items-center mb-4">
                          <Shield className="h-6 w-6 mr-2" />
                          <h3 className="text-lg font-bold">Simulation Summary</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold">{results.meanCoverageRatio?.toFixed(0) || 'N/A'}%</div>
                            <div className="text-sm text-blue-100">Mean Coverage</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold">{results.defaultRate.toFixed(1)}%</div>
                            <div className="text-sm text-blue-100">Default Rate</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-3xl font-bold ${results.btcPriceChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                              {results.btcPriceChange >= 0 ? '+' : ''}{results.btcPriceChange.toFixed(1)}%
                            </div>
                            <div className="text-sm text-blue-100">BTC Price Δ</div>
                          </div>
                        </div>
                      </Card>

                      <Card className="bg-white border-gray-200 p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-[#2A2A33] mb-4 flex items-center">
                          <DollarSign className="mr-2 h-5 w-5 text-green-600" />
                          Bank Performance
                        </h3>
                        
                        <div className="space-y-4">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="text-sm text-[#6B6B76]">Mean Profit</div>
                            <div className="text-3xl font-bold text-green-600">{fmt(results.bank.meanProfit)}</div>
                            <div className="text-sm text-green-600">ROI: {results.bank.roi.toFixed(1)}%</div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-[#F7F7F7] rounded-lg p-3">
                              <div className="text-xs text-[#6B6B76]">5th Percentile</div>
                              <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile5)}</div>
                            </div>
                            <div className="bg-[#F7F7F7] rounded-lg p-3">
                              <div className="text-xs text-[#6B6B76]">95th Percentile</div>
                              <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile95)}</div>
                            </div>
                          </div>

                          <div className={`rounded-lg p-4 ${results.bank.lossProbability < 5 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                            <div className="text-sm text-[#6B6B76]">Bank Loss Probability</div>
                            <div className={`text-2xl font-bold ${results.bank.lossProbability < 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {results.bank.lossProbability.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </Card>

                      <Card className="bg-white border-gray-200 p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Coverage Distribution</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-[#6B6B76]">Minimum Coverage</span>
                            <span className={`font-semibold ${results.minCoverage >= 100 ? 'text-green-600' : 'text-red-600'}`}>
                              {results.minCoverage.toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#6B6B76]">Mean Coverage</span>
                            <span className="font-semibold text-[#006AFF]">{results.meanCoverageRatio.toFixed(0)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#6B6B76]">Maximum Coverage</span>
                            <span className="font-semibold text-green-600">{results.maxCoverage.toFixed(0)}%</span>
                          </div>
                          <div className="flex justify-between pt-3 border-t">
                            <span className="text-[#6B6B76]">Mean BTC Price (End)</span>
                            <span className="font-semibold text-[#2A2A33]">${results.meanBtcPrice.toLocaleString()}</span>
                          </div>
                        </div>
                      </Card>
                    </>
                  ) : (
                    <Card className="bg-white border-gray-200 p-12 text-center shadow-sm">
                      <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-[#6B6B76] text-lg mb-2">No simulation results yet</p>
                      <p className="text-[#9B9BA5] text-sm">Adjust parameters and run the Monte Carlo simulation to analyze this deal for {property.name}</p>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* De-Risk Analysis Panel */}
      {property && (
        <DeRiskAnalysisPanel 
          property={property}
          isOpen={showDeRiskPanel}
          onClose={() => setShowDeRiskPanel(false)}
        />
      )}

      <Footer />
    </div>
  );
};

export default CREPropertyDetail;
